/**
 * Warm the /products route chunk (Products.tsx + ProductGrid + its parallel
 * dep chunks) into the module cache before the user navigates there.
 *
 * WHY: on a cold homepage load, `Products-*.js` isn't even requested until the
 * entry+vendor JS has downloaded and React Router mounts (~4.4s on throttled
 * mobile — S57 "gate B"). Kicking off the same dynamic import that
 * `React.lazy(() => import('@/pages/Products'))` in App.tsx uses, on
 * hover/focus/touch or homepage idle, means the chunk is already there when the
 * click lands. Rollup dedupes the two `import('@/pages/Products')` sites to one
 * chunk, so this doesn't add weight — it just moves the fetch earlier.
 *
 * Best-effort: any failure is swallowed and the flag reset, so the real
 * navigation still triggers a fresh load.
 */
let started = false;

export function prefetchProductsRoute(): void {
  if (started) return;
  started = true;
  import('@/pages/Products').catch(() => {
    started = false;
  });
}

/**
 * Spread onto a <Link to="/products">. Fires on the earliest signal that a click
 * is coming — pointer hover (desktop), keyboard focus (a11y / tab nav), and
 * touchstart (mobile, which has no hover — the ~90ms between touchstart and the
 * navigation is still worth a head start).
 */
export const productsPrefetchHandlers = {
  onMouseEnter: prefetchProductsRoute,
  onFocus: prefetchProductsRoute,
  onTouchStart: prefetchProductsRoute,
} as const;
